import { Validators } from '@angular/forms';

import {
    CloseoutDeEscalationEnum,
    CloseoutPlanAttachedAccountListItemModel,
    CloseoutPlanDetailInterface,
    CloseoutPlanListItemModel,
    CloseoutPlanSearchRequestModel,
    CloseoutPlanStatusEnum,
    CloseoutPlanStatusTriggerEnum,
    CloseoutPlanTypeEnum,
    CreateCloseoutPlanRequestModel,
    UpdateCloseoutPlanRequestModel
} from '@spa/api';
import { AmountMaxValidator, AmountMinValidator, OnlyDigitsValidator } from '@spa/validators';

import { ColorCodesEnum, ComponentTypesEnum } from '../../app/shared/enums';
import { FilterControlInterface } from '../../app/shared/interfaces';
import { AmountControl, BaseControl, Helper, RadioGroupControl, SelectControl } from '../../app/shared/classes';
import { RadioGroupOptionInterface } from '../../app/shared/components/radio-group/interfaces/radio-group-option.interface';
import {
    DialogButtonTypesEnum,
    DialogConfigInterface,
    ReportTableColumn,
    ReportTableConfigInterface,
    ReportTableData
} from '../../app/shared/components';
import { SelectOptionInterface } from '../../app/shared/components/select/interfaces/select-option.interface';

// ── Selectors ──
export const PanelCloseoutPlansStorageId = 'PanelSettingsCloseoutPlansComponent';

// ── Validation ──
export const CloseoutPlanNameMaxLength = 128;
export const CloseoutPlanThresholdsValidatorKey = 'closeoutPlanThresholds';

/**
 * BR-6: thresholds are optional integers 0-100; an empty one means the band is not used.
 * `app-amount-input [onlyIntegers]` blocks the decimal separator at the keystroke;
 * `OnlyDigitsValidator` covers a pasted value.
 */
export const CloseoutPlanThresholdValidators = () => [
    OnlyDigitsValidator(),
    AmountMinValidator(0),
    AmountMaxValidator(100)
];

/**
 * Messages for the closeout-plan-specific validator keys, merged over the shared form messages.
 * Keys the shared messages already cover are deliberately absent — they interpolate the bound
 * reported by the validator itself.
 */
export const CloseoutPlanFormMessages = (): { [key: string]: string } => ({
    [CloseoutPlanThresholdsValidatorKey]:
        'Filled thresholds must be monotonic (Warning ≤ Close-cancel ≤ Liquidation).'
});

// ── Enum option builders ──
const CloseoutPlanStatusOptions = (): SelectOptionInterface[] => [
    CloseoutPlanStatusEnum.New,
    CloseoutPlanStatusEnum.Active,
    CloseoutPlanStatusEnum.Inactive
].map((status: number) => ({ label: Helper.textsHelper().getCloseoutPlanStatusName(status), value: status }));

const CloseoutPlanTypeOptions = (): SelectOptionInterface[] => [
    {
        label: Helper.textsHelper().getCloseoutPlanTypeName(CloseoutPlanTypeEnum.UnrealizedLossPctOfCash),
        value: CloseoutPlanTypeEnum.UnrealizedLossPctOfCash
    }
];

// ── Filter Controls ──
export const PanelCloseoutPlansFilterControls = (): FilterControlInterface[] => [
    {
        name: CloseoutPlanSearchRequestModel.getNameSelector(),
        placeholder: 'Name',
        controls: [
            new BaseControl({
                name: CloseoutPlanSearchRequestModel.getNameSelector(),
                value: null,
                placeholder: 'Name',
                validators: [],
                componentType: ComponentTypesEnum.Text
            })
        ],
        sortIndex: 1
    },
    {
        name: CloseoutPlanSearchRequestModel.getStatusSelector(),
        placeholder: 'Status',
        controls: [
            new SelectControl({
                name: CloseoutPlanSearchRequestModel.getStatusSelector(),
                value: null,
                placeholder: 'Status',
                validators: [],
                componentType: ComponentTypesEnum.Select,
                options: [
                    { label: 'All', value: null },
                    ...CloseoutPlanStatusOptions()
                ]
            })
        ],
        sortIndex: 2
    }
];

// ── Data Table Items (initial state) ──
export const PanelCloseoutPlansDataTableItems = (): ReportTableData[] => {
    const item = (colId: string) => new ReportTableData<string>({ colId, value: null, hidden: false });

    return [
        item(CloseoutPlanListItemModel.getIdSelector()),
        item(CloseoutPlanListItemModel.getNameSelector()),
        item(CloseoutPlanListItemModel.getTypeSelector()),
        item(CloseoutPlanListItemModel.getWarningThresholdPctSelector()),
        item(CloseoutPlanListItemModel.getCloseCancelThresholdPctSelector()),
        item(CloseoutPlanListItemModel.getLiquidationThresholdPctSelector()),
        item(CloseoutPlanListItemModel.getDeEscalationSelector()),
        item(CloseoutPlanListItemModel.getAccountsCountSelector()),
        item(CloseoutPlanListItemModel.getStatusSelector()),
        item(CloseoutPlanListItemModel.getCreatedOnSelector()),
        item(CloseoutPlanListItemModel.getCreatedByUserNameSelector()),
    ];
};

/**
 * The endpoints send enums as numbers, so those columns need their label substituted before the
 * row is rendered. `id` sorts server-side on the entity PK while the cell shows the derived
 * RP-XXXXXX display id.
 */
export const PanelCloseoutPlansColumnFormatters: { [colId: string]: (item: any) => any } = {
    [CloseoutPlanListItemModel.getIdSelector()]: (item: any) => item.display_id,
    [CloseoutPlanListItemModel.getTypeSelector()]: (item: any) =>
        Helper.textsHelper().getCloseoutPlanTypeName(item.type),
    [CloseoutPlanListItemModel.getDeEscalationSelector()]: (item: any) =>
        Helper.textsHelper().getCloseoutDeEscalationName(item.de_escalation),
    [CloseoutPlanListItemModel.getStatusSelector()]: (item: any) =>
        Helper.textsHelper().getCloseoutPlanStatusName(item.status),
    [CloseoutPlanListItemModel.getCreatedOnSelector()]: (item: any) =>
        Helper.formatDate(item.created_on)
};

// ── Data Table Config (column definitions) ──

/**
 * Only entity-backed columns are sortable: the search endpoint orders through dynamic LINQ on the
 * `CloseoutPlan` entity, so a computed (`accounts_count`, `display_id`) or navigation
 * (`created_by_user_name`) column would throw server-side. `id` is backed by the PK.
 */
export const PanelCloseoutPlansDataTableConfig = (): ReportTableConfigInterface => {
    const column = (label: string, selector: string, sortable: boolean) =>
        new ReportTableColumn({ label, selector, sortable });

    return {
        rows: [],
        columns: [
            column('ID',             CloseoutPlanListItemModel.getIdSelector(),                        true),
            column('Name',           CloseoutPlanListItemModel.getNameSelector(),                      true),
            column('Type',           CloseoutPlanListItemModel.getTypeSelector(),                      true),
            column('Warning %',      CloseoutPlanListItemModel.getWarningThresholdPctSelector(),       true),
            column('Close-cancel %', CloseoutPlanListItemModel.getCloseCancelThresholdPctSelector(),   true),
            column('Liquidation %',  CloseoutPlanListItemModel.getLiquidationThresholdPctSelector(),   true),
            column('De-escalation',  CloseoutPlanListItemModel.getDeEscalationSelector(),              true),
            column('Accounts',       CloseoutPlanListItemModel.getAccountsCountSelector(),             false),
            column('Status',         CloseoutPlanListItemModel.getStatusSelector(),                    true),
            column('Created On',     CloseoutPlanListItemModel.getCreatedOnSelector(),                 true),
            column('Created By',     CloseoutPlanListItemModel.getCreatedByUserNameSelector(),         false),
        ],
        hiddenColumnIds: [],
        actionMenu: false
    };
};

// ── Create Closeout Plan Dialog Config ──
export const CreateCloseoutPlanDialogConfig = (): DialogConfigInterface => {
    return {
        process: {
            loading: false,
            finished: false,
            failed: false,
            failedMessage: '',
            succeedMessage: ''
        },
        buttons: [
            {
                type: DialogButtonTypesEnum.Cancel
            },
            {
                type: DialogButtonTypesEnum.Create
            }
        ],
        buttonsDisabled: false,
        buttonsHidden: false
    };
};

// ── Create Closeout Plan Form Controls Interface ──
export interface CreateCloseoutPlanFormControlsInterface {
    name: BaseControl;
    type: SelectControl;
    warningThresholdPct: AmountControl;
    closeCancelThresholdPct: AmountControl;
    liquidationThresholdPct: AmountControl;
    deEscalation: SelectControl;
}

/**
 * Phase 1: exactly one plan type (D6) and a fixed de-escalation (F3) — both server-assigned, so
 * they render as disabled display controls and never reach the request (disabled controls are
 * excluded from `form.value`).
 */
export const CreateCloseoutPlanControls = (): CreateCloseoutPlanFormControlsInterface => ({
    name: new BaseControl({
        name: CreateCloseoutPlanRequestModel.getNameSelector(),
        value: '',
        placeholder: 'Name',
        validators: [Validators.required, Validators.maxLength(CloseoutPlanNameMaxLength)],
        componentType: ComponentTypesEnum.Text
    }),
    type: new SelectControl({
        name: 'type',
        value: CloseoutPlanTypeEnum.UnrealizedLossPctOfCash,
        placeholder: 'Type',
        validators: [],
        componentType: ComponentTypesEnum.Select,
        options: CloseoutPlanTypeOptions(),
        disabled: true
    }),
    warningThresholdPct: new AmountControl({
        name: CreateCloseoutPlanRequestModel.getWarningThresholdPctSelector(),
        value: null,
        placeholder: 'Warning threshold %',
        validators: CloseoutPlanThresholdValidators(),
        componentType: ComponentTypesEnum.AmountInput,
        currency: null,
        isPercentage: true
    }),
    closeCancelThresholdPct: new AmountControl({
        name: CreateCloseoutPlanRequestModel.getCloseCancelThresholdPctSelector(),
        value: null,
        placeholder: 'Close-cancel threshold %',
        validators: CloseoutPlanThresholdValidators(),
        componentType: ComponentTypesEnum.AmountInput,
        currency: null,
        isPercentage: true
    }),
    liquidationThresholdPct: new AmountControl({
        name: CreateCloseoutPlanRequestModel.getLiquidationThresholdPctSelector(),
        value: null,
        placeholder: 'Liquidation threshold %',
        validators: CloseoutPlanThresholdValidators(),
        componentType: ComponentTypesEnum.AmountInput,
        currency: null,
        isPercentage: true
    }),
    deEscalation: new SelectControl({
        name: 'de_escalation',
        value: CloseoutDeEscalationEnum.KeepMostSevereActionInPlace,
        placeholder: 'De-escalation',
        validators: [],
        componentType: ComponentTypesEnum.Select,
        options: [
            {
                label: Helper.textsHelper().getCloseoutDeEscalationName(CloseoutDeEscalationEnum.KeepMostSevereActionInPlace),
                value: CloseoutDeEscalationEnum.KeepMostSevereActionInPlace
            }
        ],
        disabled: true
    })
});

// ── Profile ──
export const PanelCloseoutPlanRequestIdSelector = 'id';

// ── Status change ──

/**
 * The triggers that land a plan on each status. `New` is terminal-as-origin — no trigger leads
 * back to it — so it only ever appears as the plan's current status, never as an offered move.
 */
export const CloseoutPlanStatusTriggers = (status: number): number[] => {
    switch (status) {
        case CloseoutPlanStatusEnum.Active:
            return [CloseoutPlanStatusTriggerEnum.Activate];
        case CloseoutPlanStatusEnum.Inactive:
            return [CloseoutPlanStatusTriggerEnum.SetInactive];
        default:
            return [];
    }
};

/** The trigger to send for a target status, or null when the API does not currently allow it. */
export const CloseoutPlanStatusTriggerFor = (status: number, availableTriggers: number[]): number => {
    const allowed = (availableTriggers || []);
    const match = CloseoutPlanStatusTriggers(status).filter((trigger: number) => allowed.indexOf(trigger) >= 0);

    return match.length > 0 ? match[0] : null;
};

export const CloseoutPlanStatusesControl = (): RadioGroupControl =>
    new RadioGroupControl({
        name: 'status',
        value: null,
        placeholder: 'Status',
        validators: [
            Validators.required
        ],
        componentType: ComponentTypesEnum.RadioGroup,
        invalid: false,
        errors: [],
        options: []
    });

export const CloseoutPlanStatusControlOptions = (): RadioGroupOptionInterface[] => [
    CloseoutPlanStatusEnum.New,
    CloseoutPlanStatusEnum.Active,
    CloseoutPlanStatusEnum.Inactive
].map((status: number) => ({
    label: Helper.textsHelper().getCloseoutPlanStatusName(status),
    value: status,
    color: ColorCodesEnum.Primary
}));

/**
 * Title/status text colouring follows the `status-*` language in `status.scss`: a plan whose rules
 * protect accounts right now reads green, a draft reads amber, a retired one red.
 */
export const CloseoutPlanStatusTextClass = (status: number): string => {
    switch (Number(status)) {
        case CloseoutPlanStatusEnum.Active:
            return 'status-active';
        case CloseoutPlanStatusEnum.New:
            return 'status-new';
        case CloseoutPlanStatusEnum.Inactive:
            return 'status-inactive';
        default:
            return '';
    }
};

// ── Update Closeout Plan Form Controls Interface ──
export interface UpdateCloseoutPlanFormControlsInterface {
    name: BaseControl;
    warningThresholdPct: AmountControl;
    closeCancelThresholdPct: AmountControl;
    liquidationThresholdPct: AmountControl;
}

/**
 * Only the plan's editable characteristics (BR-6, spec §2.5): name and the three thresholds.
 * Type and de-escalation are fixed at creation and never offered here.
 */
export const UpdateCloseoutPlanControls = (plan: CloseoutPlanDetailInterface): UpdateCloseoutPlanFormControlsInterface => ({
    name: new BaseControl({
        name: UpdateCloseoutPlanRequestModel.getNameSelector(),
        value: plan?.name || '',
        placeholder: 'Name',
        validators: [Validators.required, Validators.maxLength(CloseoutPlanNameMaxLength)],
        componentType: ComponentTypesEnum.Text
    }),
    warningThresholdPct: new AmountControl({
        name: UpdateCloseoutPlanRequestModel.getWarningThresholdPctSelector(),
        value: plan?.warning_threshold_pct?.toString() ?? null,
        placeholder: 'Warning threshold %',
        validators: CloseoutPlanThresholdValidators(),
        componentType: ComponentTypesEnum.AmountInput,
        currency: null,
        isPercentage: true
    }),
    closeCancelThresholdPct: new AmountControl({
        name: UpdateCloseoutPlanRequestModel.getCloseCancelThresholdPctSelector(),
        value: plan?.close_cancel_threshold_pct?.toString() ?? null,
        placeholder: 'Close-cancel threshold %',
        validators: CloseoutPlanThresholdValidators(),
        componentType: ComponentTypesEnum.AmountInput,
        currency: null,
        isPercentage: true
    }),
    liquidationThresholdPct: new AmountControl({
        name: UpdateCloseoutPlanRequestModel.getLiquidationThresholdPctSelector(),
        value: plan?.liquidation_threshold_pct?.toString() ?? null,
        placeholder: 'Liquidation threshold %',
        validators: CloseoutPlanThresholdValidators(),
        componentType: ComponentTypesEnum.AmountInput,
        currency: null,
        isPercentage: true
    })
});

// ── Profile: Accounts tab ──

/**
 * No cache restore for this table: the storage key is plan-agnostic, so a cached page/sort from
 * one plan would silently bleed into another plan's tab. Every visit starts from the defaults.
 */
export const PanelCloseoutPlanAttachedAccountsStorageId = 'PanelSettingsCloseoutPlanProfileAccountsComponent';

export const PanelCloseoutPlanAttachedAccountsDataTableItems = (): ReportTableData[] => {
    const item = (colId: string) => new ReportTableData<string>({ colId, value: null, hidden: false });

    return [
        item(CloseoutPlanAttachedAccountListItemModel.getAccountReferenceIdSelector()),
        item(CloseoutPlanAttachedAccountListItemModel.getClientNameSelector()),
        item(CloseoutPlanAttachedAccountListItemModel.getPlatformNameSelector()),
        item(CloseoutPlanAttachedAccountListItemModel.getGroupNameSelector()),
        item(CloseoutPlanAttachedAccountListItemModel.getAttachedOnColumnSelector()),
        item(CloseoutPlanAttachedAccountListItemModel.getAttachedByUserNameSelector())
    ];
};

/**
 * The Attached On column is keyed by the entity-backed sort name (dynamic-LINQ constraint, see
 * the column config) while the wire field is `attached_on`. Nulls render as a dash - attach
 * metadata only exists for accounts attached after CRM-5260.
 */
export const PanelCloseoutPlanAttachedAccountsColumnFormatters: { [colId: string]: (item: any) => any } = {
    [CloseoutPlanAttachedAccountListItemModel.getClientNameSelector()]: (item: any) => item.client_name || '—',
    [CloseoutPlanAttachedAccountListItemModel.getPlatformNameSelector()]: (item: any) => item.platform_name || '—',
    [CloseoutPlanAttachedAccountListItemModel.getGroupNameSelector()]: (item: any) => item.group_name || '—',
    [CloseoutPlanAttachedAccountListItemModel.getAttachedOnColumnSelector()]: (item: any) =>
        item.attached_on ? Helper.formatDate(item.attached_on) : '—',
    [CloseoutPlanAttachedAccountListItemModel.getAttachedByUserNameSelector()]: (item: any) =>
        item.attached_by_user_name || '—'
};

/**
 * Only entity-backed columns are sortable: the search endpoint orders through dynamic LINQ on the
 * `Account` entity, so the client/platform/group join names and the attached-by subquery would
 * throw server-side. `account_reference_id` and `closeout_plan_attached_on` are entity properties.
 */
export const PanelCloseoutPlanAttachedAccountsDataTableConfig = (): ReportTableConfigInterface => {
    const column = (label: string, selector: string, sortable: boolean) =>
        new ReportTableColumn({ label, selector, sortable });

    return {
        rows: [],
        columns: [
            column('Account',     CloseoutPlanAttachedAccountListItemModel.getAccountReferenceIdSelector(), true),
            column('Client',      CloseoutPlanAttachedAccountListItemModel.getClientNameSelector(),         false),
            column('Platform',    CloseoutPlanAttachedAccountListItemModel.getPlatformNameSelector(),       false),
            column('Group',       CloseoutPlanAttachedAccountListItemModel.getGroupNameSelector(),          false),
            column('Attached On', CloseoutPlanAttachedAccountListItemModel.getAttachedOnColumnSelector(),   true),
            column('Attached By', CloseoutPlanAttachedAccountListItemModel.getAttachedByUserNameSelector(), false),
        ],
        hiddenColumnIds: [],
        actionMenu: false
    };
};

// ── Attach / Detach Dialog Configs ──
export const AttachCloseoutPlanAccountsDialogConfig = (): DialogConfigInterface => {
    return {
        process: {
            loading: false,
            finished: false,
            failed: false,
            failedMessage: '',
            succeedMessage: ''
        },
        buttons: [
            {
                type: DialogButtonTypesEnum.Cancel
            },
            {
                type: DialogButtonTypesEnum.Confirm
            }
        ],
        buttonsDisabled: false,
        buttonsHidden: false
    };
};

export const DetachCloseoutPlanAccountsDialogConfig = (): DialogConfigInterface => {
    return {
        process: {
            loading: false,
            finished: false,
            failed: false,
            failedMessage: '',
            succeedMessage: ''
        },
        buttons: [
            {
                type: DialogButtonTypesEnum.Cancel
            },
            {
                type: DialogButtonTypesEnum.Confirm
            }
        ],
        buttonsDisabled: false,
        buttonsHidden: false
    };
};
